import { useState, useEffect, useMemo } from 'react'
import Header from '@/components/Header'
import Footer from '@/components/Footer'

// ─── Shipment types ───────────────────────────────────────────────────────────

interface ShipmentRecord {
    rowKey: string
    requested_by: string
    recipient_name: string
    recipient_email: string
    product_id: number
    delivery_type: number
    status: string
    timestamp: string
    orderId: string
}

type ShipmentSortKey = 'timestamp' | 'requested_by' | 'recipient_name' | 'status'

const PRODUCT_NAMES: Record<number, string> = {
    1: 'YubiKey 5 NFC',
    2: 'YubiKey 5 Nano',
    3: 'YubiKey 5C',
    4: 'YubiKey 5C Nano',
    5: 'YubiKey 5Ci',
    29: 'YubiKey 5C NFC',
}

// ─── Event request types ──────────────────────────────────────────────────────

interface EventRequest {
    rowKey: string
    firstName: string
    lastName: string
    email: string
    phone: string
    company: string
    street: string
    city: string
    state: string
    zip: string
    country: string
    timestamp: string
    shipmentStatus?: string  // 'none' | 'pending' | 'approved' | 'rejected'
    shipmentRowKey?: string
}

type EventSortKey = 'timestamp' | 'lastName' | 'company'
type SortDir = 'asc' | 'desc'

// ─── Shared components ────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
    if (status === 'approved') {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">Approved</span>
    }
    if (status === 'rejected') {
        return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">Rejected</span>
    }
    return <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">Pending</span>
}

function ShipmentStatusBadge({ status }: { status?: string }) {
    if (status === 'approved') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700"><i className="iconoir-check text-xs"></i>Shipped</span>
    }
    if (status === 'rejected') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700"><i className="iconoir-xmark text-xs"></i>Rejected</span>
    }
    if (status === 'pending') {
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700"><i className="iconoir-clock text-xs"></i>Pending</span>
    }
    return null
}

function SortIcon<T extends string>({ col, sortKey, sortDir }: { col: T; sortKey: T; sortDir: SortDir }) {
    if (col !== sortKey) return <span className="ml-1 opacity-40">↕</span>
    return <span className="ml-1">{sortDir === 'asc' ? '↑' : '↓'}</span>
}

// ─── CSV export ───────────────────────────────────────────────────────────────

function exportShipmentCSV(rows: ShipmentRecord[]) {
    const headers = ['Date', 'Requested By', 'Recipient', 'Email', 'Product', 'Delivery', 'Status', 'Order ID']
    const lines = rows.map(s => [
        new Date(s.timestamp).toLocaleDateString(),
        s.requested_by || '',
        s.recipient_name,
        s.recipient_email,
        PRODUCT_NAMES[s.product_id] || `Product ${s.product_id}`,
        s.delivery_type === 2 ? 'Expedited' : 'Standard',
        s.status,
        s.orderId || ''
    ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
    downloadCSV([headers.join(','), ...lines].join('\n'), 'shipment-report')
}

function exportEventCSV(rows: EventRequest[]) {
    const headers = ['Date', 'First Name', 'Last Name', 'Email', 'Phone', 'Company', 'Street', 'City', 'State', 'Zip', 'Country', 'Shipment Status']
    const lines = rows.map(r => [
        new Date(r.timestamp).toLocaleDateString(),
        r.firstName, r.lastName, r.email, r.phone, r.company,
        r.street, r.city, r.state, r.zip, r.country || '',
        r.shipmentStatus && r.shipmentStatus !== 'none' ? r.shipmentStatus : 'not requested'
    ].map(v => `"${String(v || '').replace(/"/g, '""')}"`).join(','))
    downloadCSV([headers.join(','), ...lines].join('\n'), 'event-registrations')
}

function downloadCSV(csv: string, name: string) {
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

// ─── Shipments tab ────────────────────────────────────────────────────────────

function ShipmentsTab() {
    const [shipments, setShipments] = useState<ShipmentRecord[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
    const [sortKey, setSortKey] = useState<ShipmentSortKey>('timestamp')
    const [sortDir, setSortDir] = useState<SortDir>('desc')
    const [deletingKey, setDeletingKey] = useState<string | null>(null)
    const [confirmKey, setConfirmKey] = useState<string | null>(null)

    useEffect(() => {
        fetch('/api/report')
            .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json() })
            .then((data: ShipmentRecord[]) => { setShipments(data); setLoading(false) })
            .catch(() => { setError('Failed to load report data.'); setLoading(false) })
    }, [])

    const filtered = useMemo(() => {
        const q = search.toLowerCase()
        return shipments
            .filter(s => statusFilter === 'all' || s.status === statusFilter)
            .filter(s =>
                !q ||
                s.requested_by?.toLowerCase().includes(q) ||
                s.recipient_name?.toLowerCase().includes(q) ||
                s.recipient_email?.toLowerCase().includes(q) ||
                s.orderId?.toLowerCase().includes(q)
            )
            .sort((a, b) => {
                let av: string = ''
                let bv: string = ''
                if (sortKey === 'timestamp') { av = a.timestamp; bv = b.timestamp }
                if (sortKey === 'requested_by') { av = a.requested_by || ''; bv = b.requested_by || '' }
                if (sortKey === 'recipient_name') { av = a.recipient_name || ''; bv = b.recipient_name || '' }
                if (sortKey === 'status') { av = a.status; bv = b.status }
                if (av < bv) return sortDir === 'asc' ? -1 : 1
                if (av > bv) return sortDir === 'asc' ? 1 : -1
                return 0
            })
    }, [shipments, search, statusFilter, sortKey, sortDir])

    function handleSort(col: ShipmentSortKey) {
        if (col === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        else { setSortKey(col); setSortDir('asc') }
    }

    async function handleDelete(rowKey: string) {
        setDeletingKey(rowKey)
        try {
            const res = await fetch(`/api/report/${rowKey}`, { method: 'DELETE' })
            if (!res.ok) throw new Error('Failed to delete')
            setShipments(prev => prev.filter(s => s.rowKey !== rowKey))
        } catch {
            setError('Failed to delete record. Please try again.')
        } finally {
            setDeletingKey(null)
            setConfirmKey(null)
        }
    }

    const thClass = "text-left px-4 py-3 font-bold whitespace-nowrap cursor-pointer select-none hover:bg-yubico-green-500 transition-colors"

    return (
        <>
            {!loading && !error && (
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    <input
                        type="text"
                        placeholder="Search requester, recipient, email, order ID..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm"
                    />
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
                        className="px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm font-medium"
                    >
                        <option value="all">All statuses</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    <button
                        onClick={() => exportShipmentCSV(filtered)}
                        disabled={filtered.length === 0}
                        className="flex items-center gap-2 px-5 py-2.5 bg-yubico-green-400 hover:bg-yubico-green-500 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl transition-all text-sm whitespace-nowrap"
                    >
                        <i className="iconoir-download text-base"></i>
                        Export CSV
                    </button>
                </div>
            )}

            {!loading && !error && (
                <p className="text-xs text-gray-400 mb-2">
                    {filtered.length} of {shipments.length} {shipments.length === 1 ? 'record' : 'records'}
                </p>
            )}

            {loading && (
                <div className="flex items-center justify-center py-20">
                    <span className="w-8 h-8 border-4 border-yubico-green-400 border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">{error}</div>}

            {!loading && !error && (
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-yubico-green-400 text-white">
                                    <th className={thClass} onClick={() => handleSort('timestamp')}>
                                        Date <SortIcon col="timestamp" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thClass} onClick={() => handleSort('requested_by')}>
                                        Requested By <SortIcon col="requested_by" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thClass} onClick={() => handleSort('recipient_name')}>
                                        Recipient <SortIcon col="recipient_name" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Email</th>
                                    <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Product</th>
                                    <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Delivery</th>
                                    <th className={thClass} onClick={() => handleSort('status')}>
                                        Status <SortIcon col="status" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className="text-left px-4 py-3 font-bold whitespace-nowrap">Order ID</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400">
                                            No records match your filters.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(s => (
                                        <tr key={s.rowKey} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                                            <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                {new Date(s.timestamp).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                {s.requested_by || '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{s.recipient_name}</td>
                                            <td className="px-4 py-3 text-gray-500">{s.recipient_email}</td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                {PRODUCT_NAMES[s.product_id] || `Product ${s.product_id}`}
                                            </td>
                                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                                                {s.delivery_type === 2 ? 'Expedited' : 'Standard'}
                                            </td>
                                            <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                                            <td className="px-4 py-3 text-gray-500 font-mono text-xs whitespace-nowrap">
                                                {s.orderId || '—'}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap text-right">
                                                {confirmKey === s.rowKey ? (
                                                    <div className="flex items-center gap-2 justify-end">
                                                        <span className="text-xs text-gray-500">Delete?</span>
                                                        <button
                                                            onClick={() => handleDelete(s.rowKey)}
                                                            disabled={deletingKey === s.rowKey}
                                                            className="px-2 py-1 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                                                        >
                                                            {deletingKey === s.rowKey ? '...' : 'Yes'}
                                                        </button>
                                                        <button
                                                            onClick={() => setConfirmKey(null)}
                                                            className="px-2 py-1 text-xs font-bold bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                                                        >
                                                            No
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setConfirmKey(s.rowKey)}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Delete record"
                                                    >
                                                        <i className="iconoir-trash text-base"></i>
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    )
}

// ─── Event registrations tab ──────────────────────────────────────────────────

function EventRegistrationsTab() {
    const [requests, setRequests] = useState<EventRequest[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [search, setSearch] = useState('')
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const [sortKey, setSortKey] = useState<EventSortKey>('timestamp')
    const [sortDir, setSortDir] = useState<SortDir>('desc')
    const [deletingKey, setDeletingKey] = useState<string | null>(null)
    const [confirmKey, setConfirmKey] = useState<string | null>(null)
    const [selected, setSelected] = useState<Set<string>>(new Set())
    const [shipping, setShipping] = useState(false)
    const [shipError, setShipError] = useState('')

    useEffect(() => {
        fetch('/api/report/event-requests')
            .then(r => { if (!r.ok) throw new Error('Failed to load'); return r.json() })
            .then((data: EventRequest[]) => { setRequests(data); setLoading(false) })
            .catch(() => { setError('Failed to load event registration data.'); setLoading(false) })
    }, [])

    const filtered = useMemo(() => {
        const q = search.toLowerCase()
        const from = dateFrom ? new Date(dateFrom).getTime() : null
        const to = dateTo ? new Date(dateTo + 'T23:59:59').getTime() : null
        return requests
            .filter(r => {
                const ts = new Date(r.timestamp).getTime()
                if (from && ts < from) return false
                if (to && ts > to) return false
                return true
            })
            .filter(r =>
                !q ||
                r.firstName?.toLowerCase().includes(q) ||
                r.lastName?.toLowerCase().includes(q) ||
                r.email?.toLowerCase().includes(q) ||
                r.company?.toLowerCase().includes(q) ||
                r.city?.toLowerCase().includes(q)
            )
            .sort((a, b) => {
                let av = ''
                let bv = ''
                if (sortKey === 'timestamp') { av = a.timestamp; bv = b.timestamp }
                if (sortKey === 'lastName') { av = a.lastName || ''; bv = b.lastName || '' }
                if (sortKey === 'company') { av = a.company || ''; bv = b.company || '' }
                if (av < bv) return sortDir === 'asc' ? -1 : 1
                if (av > bv) return sortDir === 'asc' ? 1 : -1
                return 0
            })
    }, [requests, search, dateFrom, dateTo, sortKey, sortDir])

    // Rows eligible for selection (no shipment already in flight)
    const selectable = filtered.filter(r => !r.shipmentStatus || r.shipmentStatus === 'none')
    const allSelectableSelected = selectable.length > 0 && selectable.every(r => selected.has(r.rowKey))

    function toggleSelectAll() {
        if (allSelectableSelected) {
            setSelected(new Set())
        } else {
            setSelected(new Set(selectable.map(r => r.rowKey)))
        }
    }

    function toggleRow(rowKey: string) {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(rowKey)) next.delete(rowKey)
            else next.add(rowKey)
            return next
        })
    }

    function handleSort(col: EventSortKey) {
        if (col === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
        else { setSortKey(col); setSortDir('asc') }
    }

    async function handleDelete(rowKey: string) {
        setDeletingKey(rowKey)
        try {
            const res = await fetch(`/api/report/event-requests/${rowKey}`, { method: 'DELETE' })
            if (!res.ok) throw new Error('Failed to delete')
            setRequests(prev => prev.filter(r => r.rowKey !== rowKey))
            setSelected(prev => { const n = new Set(prev); n.delete(rowKey); return n })
        } catch {
            setError('Failed to delete record. Please try again.')
        } finally {
            setDeletingKey(null)
            setConfirmKey(null)
        }
    }

    async function handleSendYubiKey() {
        const rowKeys = Array.from(selected)
        if (rowKeys.length === 0) return
        setShipping(true)
        setShipError('')
        try {
            const res = await fetch('/api/report/event-requests/ship', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rowKeys }),
            })
            const body = await res.json()
            const failures = (body.results ?? []).filter((r: any) => !r.success)
            if (failures.length > 0) {
                setShipError(`${failures.length} request(s) failed: ${failures.map((f: any) => f.error).join(', ')}`)
            }
            // Update local state to reflect pending status for successful rows
            const succeeded = new Set((body.results ?? []).filter((r: any) => r.success).map((r: any) => r.rowKey))
            setRequests(prev => prev.map(r =>
                succeeded.has(r.rowKey) ? { ...r, shipmentStatus: 'pending' } : r
            ))
            setSelected(new Set())
        } catch {
            setShipError('Failed to send shipping requests. Please try again.')
        } finally {
            setShipping(false)
        }
    }

    const thClass = "text-left px-4 py-3 font-bold whitespace-nowrap cursor-pointer select-none hover:bg-yubico-green-500 transition-colors"
    const thStatic = "text-left px-4 py-3 font-bold whitespace-nowrap"

    return (
        <>
            {!loading && !error && (
                <div className="flex flex-col gap-3 mb-4">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            placeholder="Search name, email, company, city..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm"
                        />
                        {selected.size > 0 && (
                            <button
                                onClick={handleSendYubiKey}
                                disabled={shipping}
                                className="flex items-center gap-2 px-5 py-2.5 bg-yubico-green-400 hover:bg-yubico-green-500 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold rounded-xl transition-all text-sm whitespace-nowrap"
                            >
                                {shipping
                                    ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    : <i className="iconoir-delivery-truck text-base"></i>
                                }
                                Send YubiKey ({selected.size})
                            </button>
                        )}
                        <button
                            onClick={() => exportEventCSV(filtered)}
                            disabled={filtered.length === 0}
                            className="flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 disabled:text-gray-300 border border-gray-200 text-gray-700 font-bold rounded-xl transition-all text-sm whitespace-nowrap"
                        >
                            <i className="iconoir-download text-base"></i>
                            Export CSV
                        </button>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                        <span className="text-sm text-gray-500 whitespace-nowrap">Date range</span>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm"
                        />
                        <span className="text-sm text-gray-400">to</span>
                        <input
                            type="date"
                            value={dateTo}
                            min={dateFrom || undefined}
                            onChange={e => setDateTo(e.target.value)}
                            className="px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-yubico-green-400 outline-none bg-white text-sm"
                        />
                        {(dateFrom || dateTo) && (
                            <button
                                onClick={() => { setDateFrom(''); setDateTo('') }}
                                className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            )}

            {shipError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl">{shipError}</div>
            )}

            {!loading && !error && (
                <p className="text-xs text-gray-400 mb-2">
                    {filtered.length} of {requests.length} {requests.length === 1 ? 'record' : 'records'}
                    {selected.size > 0 && ` · ${selected.size} selected`}
                </p>
            )}

            {loading && (
                <div className="flex items-center justify-center py-20">
                    <span className="w-8 h-8 border-4 border-yubico-green-400 border-t-transparent rounded-full animate-spin" />
                </div>
            )}

            {error && <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">{error}</div>}

            {!loading && !error && (
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-yubico-green-400 text-white">
                                    <th className="px-4 py-3">
                                        <input
                                            type="checkbox"
                                            checked={allSelectableSelected}
                                            onChange={toggleSelectAll}
                                            disabled={selectable.length === 0}
                                            className="w-4 h-4 accent-white cursor-pointer disabled:opacity-40"
                                            title="Select all"
                                        />
                                    </th>
                                    <th className={thClass} onClick={() => handleSort('timestamp')}>
                                        Date <SortIcon col="timestamp" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thClass} onClick={() => handleSort('lastName')}>
                                        Name <SortIcon col="lastName" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thStatic}>Email</th>
                                    <th className={thStatic}>Phone</th>
                                    <th className={thClass} onClick={() => handleSort('company')}>
                                        Company <SortIcon col="company" sortKey={sortKey} sortDir={sortDir} />
                                    </th>
                                    <th className={thStatic}>Address</th>
                                    <th className={thStatic}>YubiKey</th>
                                    <th className="px-4 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="text-center py-12 text-gray-400">
                                            No registrations yet.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(r => {
                                        const isSelectable = !r.shipmentStatus || r.shipmentStatus === 'none'
                                        const isSelected = selected.has(r.rowKey)
                                        return (
                                            <tr
                                                key={r.rowKey}
                                                className={`border-t border-gray-100 transition-colors ${isSelected ? 'bg-yubico-green-50' : 'hover:bg-gray-50'}`}
                                            >
                                                <td className="px-4 py-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={!isSelectable}
                                                        onChange={() => toggleRow(r.rowKey)}
                                                        className="w-4 h-4 accent-yubico-green-400 cursor-pointer disabled:opacity-30"
                                                    />
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                                                    {new Date(r.timestamp).toLocaleDateString()}
                                                </td>
                                                <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                                                    {r.firstName} {r.lastName}
                                                </td>
                                                <td className="px-4 py-3 text-gray-500">{r.email}</td>
                                                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{r.phone || '—'}</td>
                                                <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{r.company}</td>
                                                <td className="px-4 py-3 text-gray-500 text-xs">
                                                    {r.street}, {r.city}, {r.state} {r.zip}{r.country ? `, ${r.country}` : ''}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <ShipmentStatusBadge status={r.shipmentStatus} />
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap text-right">
                                                    {confirmKey === r.rowKey ? (
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <span className="text-xs text-gray-500">Delete?</span>
                                                            <button
                                                                onClick={() => handleDelete(r.rowKey)}
                                                                disabled={deletingKey === r.rowKey}
                                                                className="px-2 py-1 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                                                            >
                                                                {deletingKey === r.rowKey ? '...' : 'Yes'}
                                                            </button>
                                                            <button
                                                                onClick={() => setConfirmKey(null)}
                                                                className="px-2 py-1 text-xs font-bold bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors"
                                                            >
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => setConfirmKey(r.rowKey)}
                                                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                            title="Delete record"
                                                        >
                                                            <i className="iconoir-trash text-base"></i>
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

type Tab = 'shipments' | 'events'

function ReportPage() {
    const [activeTab, setActiveTab] = useState<Tab>('shipments')

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <Header variant="default" />

            <main className="flex-grow p-6 py-12">
                <div className="max-w-7xl mx-auto">

                    {/* Header */}
                    <div className="mb-6">
                        <div className="inline-block p-3 bg-yubico-green-50 rounded-2xl mb-4">
                            <i className="iconoir-stats-report text-yubico-green-400 text-3xl"></i>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900">Reports</h2>
                        <p className="text-gray-500 mt-1">Shipment requests and event registrations logged in Azure Table Storage</p>
                    </div>

                    {/* Tabs */}
                    <div className="flex gap-1 mb-6 border-b border-gray-200">
                        <button
                            onClick={() => setActiveTab('shipments')}
                            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                                activeTab === 'shipments'
                                    ? 'bg-white border border-b-white border-gray-200 text-yubico-green-500 -mb-px'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <i className="iconoir-delivery-truck mr-2 text-sm"></i>
                            Shipment Requests
                        </button>
                        <button
                            onClick={() => setActiveTab('events')}
                            className={`px-5 py-2.5 text-sm font-semibold rounded-t-lg transition-colors ${
                                activeTab === 'events'
                                    ? 'bg-white border border-b-white border-gray-200 text-yubico-green-500 -mb-px'
                                    : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            <i className="iconoir-qr-code mr-2 text-sm"></i>
                            Event Registrations
                        </button>
                    </div>

                    {activeTab === 'shipments' ? <ShipmentsTab /> : <EventRegistrationsTab />}
                </div>
            </main>

            <Footer variant="default" />
        </div>
    )
}

export default ReportPage
